import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Users, AlertCircle } from 'lucide-react';

import { Breadcrumb } from '../components/Breadcrumb';
import { CustomerSearchSelect } from '../components/customers/CustomerSearchSelect';
import { CustomerHeaderCard } from '../components/customers/CustomerHeaderCard';
import { CustomerHistoryTable } from '../components/customers/CustomerHistoryTable';
import { apiService } from '../services/apiService';
import {
  CustomerDetail,
  CustomerQuoteHistoryItem,
  CustomerOrderHistoryItem,
  CustomerSearchResult,
} from '../services/types';

interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export const CustomerDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { custNum: custNumParam } = useParams<{ custNum?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const custNum = custNumParam ? parseInt(custNumParam, 10) : null;
  const shipToNum = searchParams.get('shipTo') || '';

  const [detail, setDetail] = useState<SectionState<CustomerDetail>>({
    data: null,
    loading: false,
    error: null,
  });
  const [quotes, setQuotes] = useState<
    SectionState<CustomerQuoteHistoryItem[]>
  >({
    data: null,
    loading: false,
    error: null,
  });
  const [orders, setOrders] = useState<
    SectionState<CustomerOrderHistoryItem[]>
  >({
    data: null,
    loading: false,
    error: null,
  });

  // Fetch customer detail (header + ship-tos) — independent of ship-to filter
  useEffect(() => {
    if (!custNum) {
      setDetail({ data: null, loading: false, error: null });
      return;
    }
    let active = true;
    setDetail({ data: null, loading: true, error: null });
    apiService
      .getCustomerDetail(custNum)
      .then((data) => {
        if (active) setDetail({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (active)
          setDetail({
            data: null,
            loading: false,
            error:
              err instanceof Error ? err.message : 'Failed to load customer',
          });
      });
    return () => {
      active = false;
    };
  }, [custNum]);

  // Fetch quote history (depends on ship-to filter)
  useEffect(() => {
    if (!custNum) {
      setQuotes({ data: null, loading: false, error: null });
      return;
    }
    let active = true;
    setQuotes({ data: null, loading: true, error: null });
    apiService
      .getCustomerQuoteHistory(custNum, shipToNum || undefined)
      .then((data) => {
        if (active) setQuotes({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (active)
          setQuotes({
            data: null,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load quotes',
          });
      });
    return () => {
      active = false;
    };
  }, [custNum, shipToNum]);

  // Fetch order history (depends on ship-to filter)
  useEffect(() => {
    if (!custNum) {
      setOrders({ data: null, loading: false, error: null });
      return;
    }
    let active = true;
    setOrders({ data: null, loading: true, error: null });
    apiService
      .getCustomerOrderHistory(custNum, shipToNum || undefined)
      .then((data) => {
        if (active) setOrders({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (active)
          setOrders({
            data: null,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load orders',
          });
      });
    return () => {
      active = false;
    };
  }, [custNum, shipToNum]);

  const handleSelectCustomer = useCallback(
    (customer: CustomerSearchResult) => {
      navigate(`/customers/${customer.custNum}`);
    },
    [navigate]
  );

  const handleShipToChange = useCallback(
    (nextShipTo: string) => {
      const params = new URLSearchParams(searchParams);
      if (nextShipTo) {
        params.set('shipTo', nextShipTo);
      } else {
        params.delete('shipTo');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const selectedShipTo =
    detail.data && shipToNum
      ? detail.data.shipTos.find((s) => s.shipToNum === shipToNum) || null
      : null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('navigation.customerDashboard')} />

        {/* Header */}
        <div className="mb-8 mt-4">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('customerDashboard.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('customerDashboard.subtitle')}
          </p>
        </div>

        <div className="space-y-6">
          {/* Search */}
          <CustomerSearchSelect
            onSelect={handleSelectCustomer}
            selectedLabel={detail.data ? detail.data.header.name : null}
          />

          {/* Detail error */}
          {custNum && detail.error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900">
                  {t('common.error')}
                </h3>
                <p className="mt-1 text-sm text-red-700">{detail.error}</p>
              </div>
            </div>
          )}

          {/* Detail loading */}
          {custNum && detail.loading && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">{t('common.loading')}...</p>
            </div>
          )}

          {/* Empty / initial state */}
          {!custNum && (
            <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
              <Users className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <p className="text-blue-900 font-medium">
                {t('customerDashboard.emptyState')}
              </p>
              <p className="text-blue-700 text-sm mt-1">
                {t('customerDashboard.searchInstructions')}
              </p>
            </div>
          )}

          {/* Customer content */}
          {custNum && detail.data && !detail.loading && (
            <>
              <CustomerHeaderCard
                header={detail.data.header}
                shipTos={detail.data.shipTos}
                selectedShipTo={selectedShipTo}
                onShipToChange={handleShipToChange}
              />

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <CustomerHistoryTable
                  variant="quote"
                  items={quotes.data ?? []}
                  loading={quotes.loading}
                  error={quotes.error}
                />
                <CustomerHistoryTable
                  variant="order"
                  items={orders.data ?? []}
                  loading={orders.loading}
                  error={orders.error}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
